import { axiosCore } from './http-core-service';
import { RequestDto } from './dtos/request.dto';
import { DirectDebitDto } from './dtos/direct-debit.dto';
import { ContractFormInfosDto } from './dtos/form/contract-form-infos.dto';
import { MeterReadingLoggedOffDto } from './dtos/meter-reading-logged-off.dto';
import { ConnectionRequestFormDto } from './dtos/form/connection-request-form.dto';
import { DirectDebitPromotionDto } from './dtos/direct-debit-promotion.dto';

/**
 * Retrieves requests for a given contract.
 *
 * @param {string|undefined} contractId - The ID of the contract (optional).
 * @returns {Promise<RequestDto[]>} - A Promise that resolves to an array of RequestDto objects.
 */
export const getRequests = async (contractId: string | undefined): Promise<RequestDto[]> => {
    const response = await axiosCore.get(`/demandes/${contractId}?pagination=false`);
    return response.data['hydra:member'];
};

/**
 * Appel API pour la récupération des informations d'un contrat
 * @param id Identifiant du contrat à récupérer
 */
export const getRequestMeterReading = async (id: string | undefined) => {
    const response = await axiosCore.get(
        'demandes/' + id + '?page=1&itemsPerPage=1&type=Déclaration%20index&etat=En%20cours'
    );
    return response.data['hydra:member'];
};

/**
 * Méthode qui stp le prélévement automatique sur un contrat
 * @param id identifiant du contrat
 */
export const stopDirectDebit = async (id: string | undefined) => {
    const response = await axiosCore.delete('demandes/' + id + '/mensualisation');
    return response.data?.message;
};

/**
 * Méthode qui permet d'ajout un prélévement automatique sur un contrat
 * @param id identifiant du contrat
 * @param data donnée
 */
export const createDirectDebit = async (id: string, data: DirectDebitDto) => {
    const response = await axiosCore.post('demandes/' + id + '/mensualisation', data);
    return response.data;
};

/**
 * Méthode qui permet d'ajout un prélévement automatique sans compte
 * @param token identifiant du contrat sans compte
 * @param data donnée
 */
export const createDirectDebitPromotion = async (token: string | null, data: DirectDebitPromotionDto) => {
    const response = await axiosCore.post(`demandes/promotion_mensualisation/${token}`, data);
    return response.data;
};

export const changeDirectDebit = async (id: string, data: DirectDebitDto) => {
    const response = await axiosCore.post(`demandes/${id}/change-coordonnees-bancaires-mensualisation`, data);
    return response.data;
};

/**
 * Méthode qui de trouver une adresse contrat d'eau via une facture
 * @param billInfos information facture pour la recherche
 */
export const getAddressByBill = async (billInfos: ContractFormInfosDto) => {
    const response = await axiosCore.get(
        'demandes/emmenagement/adresse_facture?referenceFacture=' +
            billInfos.referenceFacture +
            '&referenceContrat=' +
            billInfos.referenceContrat +
            '&montantFacture=' +
            billInfos.montantFacture
    );
    return response.data;
};

/**
 * Méthode qui permet de récupérer une addresse compteur en fonction d'une adresse demandé
 * @param codeInsee code insee
 * @param street rue
 * @param postalCode Code postal
 * @param city ville
 * @param streetNumber numéro
 */
export const getAddress = async (
    codeInsee?: string,
    streetNumber?: string,
    street?: string,
    postalCode?: string,
    city?: string
) => {
    let params = '';
    params += codeInsee ? '&codeInsee=' + codeInsee : '';
    params += streetNumber ? '&numeroRue=' + streetNumber : '';
    params += street ? '&rue=' + street : '';
    params += postalCode ? '&codePostal=' + postalCode : '';
    params += city ? '&ville=' + city : '';

    const response = await axiosCore.get('demandes/emmenagement/liste_adresse?' + params);
    return response.data;
};

/**
 * Appel API qui permet de créer un relevè sur un rattachement de contrat non connecté.
 * @param data données du relevè + facture
 */
export const createMeterReadingLoggedOff = async (data: MeterReadingLoggedOffDto): Promise<any> => {
    const response = await axiosCore.post('/demandes/releves', data);
    return response.data;
};

/**
 * Sends a request for connection with Arabic Certification (AC).
 *
 * @param {string | Blob} file - The file to be sent for connection.
 * @param {string} [captcha] - Optional captcha value.
 * @param {ConnectionRequestFormDto} values - valeurs du formulaire.
 * @returns {Promise<any>} - A promise that resolves to the response data.
 */
export const sendConnectionACRequest = async (
    file: string | Blob,
    values: ConnectionRequestFormDto,
    captcha?: string
): Promise<any> => {
    // Création du body de type formData
    const formData = new FormData();
    formData.append('files', file);
    if (captcha) {
        formData.append('captcha', captcha);
    }
    formData.append('data', JSON.stringify(values));
    const response = await axiosCore.post(`/demandes/raccordementac`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};
